import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// Custom lock to prevent cross-tab Navigator LockManager from freezing on refresh
const customLock = async <R>(name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return fn();
  }
  return new Promise<R>((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        fn().then(resolve, reject);
      }
    }, 10_000);

    navigator.locks
      .request(name, { mode: "exclusive" }, async () => {
        if (settled) return;
        try {
          const result = await fn();
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(result);
        } catch (err) {
          settled = true;
          window.clearTimeout(timeoutId);
          reject(err);
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeoutId);
          reject(err);
        }
      });
  });
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: customLock,
  },
});

export type ProductMedia = { url: string; type: string };

export interface ProductCategory {
  id: number;
  name: string;
  sort_order: number;
}

export interface ProductItem {
  id: number;
  name: string;
  category_id: number | null;
  status: string;
  description: string | null;
  pricing_mode: number;
  currency: string;
  price: number | string | null;
  stock: number | null;
  discount_enabled: boolean;
  discount_price: number | string | null;
  media: ProductMedia[];
  product_categories?: ProductCategory | null;
}

export interface ProductVariant {
  id: number;
  product_id: number;
  name: string;
  options: string[];
  sort_order: number;
}

export interface ProductSku {
  id: number;
  product_id: number;
  label: string;
  options: string[];
  price: number | string | null;
  stock: number | null;
  discount_enabled: boolean;
  discount_price: number | string | null;
}